<template>
  <v-card flat class="pa-4">
    <v-card-title class="headline">Синхронизация с AmoCRM</v-card-title>

    <v-card-text>
      <v-alert v-if="!isAmoCrmEnabled" type="warning" dense>
        Для синхронизации необходимо включить и настроить интеграцию с AmoCRM
      </v-alert>

      <v-row v-if="isAmoCrmEnabled">
        <v-col cols="12" sm="6" md="3">
          <v-card outlined>
            <v-card-title class="subtitle-1">Автомобили</v-card-title>
            <v-card-text>
              <div class="text-center mb-2">
                <v-chip label>{{ syncStats.cars || 0 }} синхронизировано</v-chip>
              </div>
              <v-btn
                block
                color="primary"
                @click="syncCars"
                :loading="loading.cars"
              >
                Синхронизировать
              </v-btn>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-card outlined>
            <v-card-title class="subtitle-1">Клиенты</v-card-title>
            <v-card-text>
              <div class="text-center mb-2">
                <v-chip label>{{ syncStats.clients || 0 }} синхронизировано</v-chip>
              </div>
              <v-btn
                block
                color="primary"
                @click="syncClients"
                :loading="loading.clients"
              >
                Синхронизировать
              </v-btn>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-card outlined>
            <v-card-title class="subtitle-1">Бронирования</v-card-title>
            <v-card-text>
              <div class="text-center mb-2">
                <v-chip label>{{ syncStats.bookings || 0 }} синхронизировано</v-chip>
              </div>
              <v-btn
                block
                color="primary"
                @click="syncBookings"
                :loading="loading.bookings"
              >
                Синхронизировать
              </v-btn>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-card outlined>
            <v-card-title class="subtitle-1">Все данные</v-card-title>
            <v-card-text>
              <div class="text-center mb-2">
                <v-chip label>Последняя синхронизация: {{ lastSyncDate }}</v-chip>
              </div>
              <v-btn
                block
                color="primary"
                @click="syncAll"
                :loading="loading.all"
              >
                Синхронизировать всё
              </v-btn>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-card-text>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000">
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">
          Закрыть
        </v-btn>
      </template>
    </v-snackbar>
  </v-card>
</template>

<script>
export default {
  name: 'AmoCrmSync',

  props: {
    companySettings: {
      type: Object,
      default: () => ({
        amocrm_on: false
      })
    }
  },

  data() {
    return {
      syncStats: {
        clients: 0,
        bookings: 0,
        cars: 0
      },
      loading: {
        clients: false,
        bookings: false,
        cars: false,
        all: false
      },
      lastSyncDate: 'Никогда',
      snackbar: {
        show: false,
        text: '',
        color: 'success'
      }
    };
  },

  computed: {
    isAmoCrmEnabled() {
      return this.companySettings.amocrm_on;
    }
  },

  mounted() {
    this.loadSyncStats();
  },

  methods: {
    loadSyncStats() {
      if (!this.isAmoCrmEnabled) return;

      this.axios
        .get('/api/v1/integrations/amocrm/stats', {
          headers: {
            Authorization: this.$store.getters.getAuthToken,
          },
        })
        .then(response => {
          this.syncStats = response.data.stats;
          this.lastSyncDate = response.data.last_sync_date || 'Никогда';
        })
        .catch(error => {
          console.error('Ошибка при загрузке статистики синхронизации', error);
        });
    },

    syncClients() {
      if (!this.isAmoCrmEnabled) return;

      this.loading.clients = true;

      this.axios
        .post('/api/v1/integrations/amocrm/sync_clients', {}, {
          headers: {
            Authorization: this.$store.getters.getAuthToken,
          },
        })
        .then(response => {
          this.showSnackbar(
            this.$t('amocrm.sync_now_started') || 'Синхронизация клиентов запущена',
            'success'
          );
        })
        .catch(error => {
          this.showSnackbar(
            this.$t('amocrm.sync_error') || 'Ошибка при запуске синхронизации',
            'error'
          );
          console.error('Ошибка при синхронизации клиентов', error);
        })
        .finally(() => {
          this.loading.clients = false;
        });
    },

    syncBookings() {
      if (!this.isAmoCrmEnabled) return;

      this.loading.bookings = true;

      this.axios
        .post('/api/v1/integrations/amocrm/sync_bookings', {}, {
          headers: {
            Authorization: this.$store.getters.getAuthToken,
          },
        })
        .then(response => {
          this.showSnackbar(
            this.$t('amocrm.sync_now_started') || 'Синхронизация бронирований запущена',
            'success'
          );
        })
        .catch(error => {
          this.showSnackbar(
            this.$t('amocrm.sync_error') || 'Ошибка при запуске синхронизации',
            'error'
          );
          console.error('Ошибка при синхронизации бронирований', error);
        })
        .finally(() => {
          this.loading.bookings = false;
        });
    },

    syncCars() {
      if (!this.isAmoCrmEnabled) return;

      this.loading.cars = true;

      this.axios
        .post('/api/v1/integrations/amocrm/sync_cars', {}, {
          headers: {
            Authorization: this.$store.getters.getAuthToken,
          },
        })
        .then(response => {
          this.showSnackbar(
            this.$t('amocrm.sync_now_started') || 'Синхронизация автомобилей запущена',
            'success'
          );
        })
        .catch(error => {
          this.showSnackbar(
            this.$t('amocrm.sync_error') || 'Ошибка при запуске синхронизации',
            'error'
          );
          console.error('Ошибка при синхронизации автомобилей', error);
        })
        .finally(() => {
          this.loading.cars = false;
        });
    },

    syncAll() {
      if (!this.isAmoCrmEnabled) return;

      this.loading.all = true;

      this.axios
        .post('/api/v1/integrations/amocrm/sync_all', {}, {
          headers: {
            Authorization: this.$store.getters.getAuthToken,
          },
        })
        .then(response => {
          this.showSnackbar(
            this.$t('amocrm.sync_now_started') || 'Полная синхронизация запущена',
            'success'
          );
          this.lastSyncDate = new Date().toLocaleString();
        })
        .catch(error => {
          this.showSnackbar(
            this.$t('amocrm.sync_error') || 'Ошибка при запуске синхронизации',
            'error'
          );
          console.error('Ошибка при полной синхронизации', error);
        })
        .finally(() => {
          this.loading.all = false;
        });
    },

    showSnackbar(text, color) {
      this.snackbar.text = text;
      this.snackbar.color = color;
      this.snackbar.show = true;
    }
  }
}
</script>
